import React, { useState, useRef, useEffect, useContext } from 'react';
import FacebookLogin from './FacebookLogin';
import GoogleLoginEdCom from './GoogleLoginEdCom';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import ReCAPTCHA from 'react-google-recaptcha';
import {
  SECTION_RESET,
  SECTION_SWITCH,
  RESPONSE_CODE_TOO_MANY_REQUESTS,
  RESPONSE_CODE_VERIFICATION_FAILED,
  RECAPTCHA_SITE_KEY,
  EMPTY_RECAPTCHA,
} from './constants';
import { trackRegistration, dispatchCustomEvent } from './util';
import {
  checkEmail,
  screenEmail,
  verifyEmailByBriteVerify,
  localLogin,
  register,
} from './api';
import AccountContext from '../../services/AccountContext';
import { FloatingLabelInput, Button } from 'edcom-ui';
import RecaptchaV3, { getRecaptchaToken } from '../RecaptchaV3';

const ERROR_MESSAGE_ENTER_EMAIL_ADDRESS = 'Please enter your email address.';
const ERROR_MESSAGE_ENTER_PASSWORD = 'Please enter your password.';
const ERROR_MESSAGE_INVALID_EMAIL = 'Invalid Email Address';
const ERROR_MESSAGE_GENERAL_FAILURE =
  'There was an error creating your account.';
const ERROR_MESSAGE_PASSWORD_SHORT =
  'Please enter a password at least 6 characters long.';
const ERROR_MESSAGE_PASSWORD_LONG =
  'Please enter a password less than 32 characters.';
const HAS_ERROR_CLASS_NAME = 'has-error';

const Signup = ({
  defaultAccount,
  setIsLoading,
  registerButtonText,
  registerDescription,
  registerTitle,
  setSection,
  gdprPreferences,
  inGDPR,
  setAccount,
  additionalInfoSection,
  setIsOpen,
  registrationUserType,
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailErrorMessage, setEmailErrorMessage] = useState('');
  const [passwordErrorMessage, setPasswordErrorMessage] = useState('');
  const [generalErrorMessage, setGeneralErrorMessage] = useState('');
  const [requireCaptcha, setRequireCaptcha] = useState(false);
  const [captchaValue, setCaptchaValue] = useState(null);
  const recaptchaRef = useRef();
  const gdprInput = useRef(null);

  const { refresh } = useContext(AccountContext);

  const ERROR_MESSAGE_EXISTING_ACCOUNT = (
    <div>
      An account with that email already exists. Please use a different email or
      <span> </span>
      <a
        onClick={() => {
          trackRegistration('forgot-password link clicked');
          setSection(SECTION_RESET);
        }}
      >
        reset your password
      </a>
      .
    </div>
  );

  useEffect(() => {
    if (defaultAccount) {
      setEmail(defaultAccount);
    }
  }, []);

  const onSignupSuccess = (existingLogin = false) => {
    // EDENG-1103: Trigger acct token refresh before additional info modal so user is logged in even if they exit the modal
    refresh()
      .then(() => {
        if (!existingLogin) {
          //TODO: Remove these custom events? I don't think we use them
          dispatchCustomEvent('member_registration');

          // Dispatch event, this is for generating token needed for the AccountContext#refresh() below, otherwise once
          // the additional popup window is closed, the user will appear to have been logged out on nextJS pages even
          // though they're actually logged in successfully already.
          dispatchCustomEvent('additionalInfoModalInit');

          // Show the additional info section
          setIsLoading(false);
          setSection(additionalInfoSection);
        } else {
          setIsLoading(false);
          setIsOpen(false);
        }
      })
      .catch(() => {
        // Some issue refreshing account state client-side, do a full reload instead
        window.location.reload();
        //TODO: If the modal fails to open what do we want to do
      });
  };

  const onSignupFailure = (data = null, code, signUpMethod) => {
    setIsLoading(false);
    if (data) {
      if (data.exists) {
        trackRegistration('Signup failure', 'failed signup', null, {
          failure_reason: 'Email in use',
        });
        setPasswordErrorMessage(ERROR_MESSAGE_EXISTING_ACCOUNT);
      } else if (!data.errors) {
        setGeneralErrorMessage(ERROR_MESSAGE_GENERAL_FAILURE);
      } else if (data.errors.emailaddress) {
        trackRegistration('Signup failure', 'failed signup', null, {
          failure_reason: 'Invalid Email',
        });
        setEmailErrorMessage(ERROR_MESSAGE_INVALID_EMAIL);
      } else if (data.errors.password) {
        // TODO: Remove this case, this error would never be returned. Check out line 397 in MembersController.php
        setPasswordErrorMessage(data.errors.password);
      } else if (data.errors.membername) {
        // TODO: Remove this case, this error would never occur with BriteVerify so member name can never be invalid
        setEmailErrorMessage(data.errors.membername);
      } else if (data.errors.gdprOptIn) {
        setGeneralErrorMessage(ERROR_MESSAGE_GENERAL_FAILURE);
      }
      if (code === RESPONSE_CODE_VERIFICATION_FAILED) {
        trackRegistration('Signup failure', 'failed signup', null, {
          failure_reason: 'Failed Recaptcha',
        });
        setGeneralErrorMessage(data.msg);
      } else if (code === RESPONSE_CODE_TOO_MANY_REQUESTS) {
        setRequireCaptcha(true);
      }
      // Only Google/Facebook sign up would fall into this case
      if (signUpMethod === 'google') {
        trackRegistration('Google Signup Failure', 'failed signup', 'google');
      }
      if (signUpMethod === 'facebook') {
        trackRegistration(
          'Facebook Signup Failure',
          'failed signup',
          'facebook'
        );
      }
    }
    // Error unrelated to a specific field
    else {
      // We will always pass a non-empty data parameter so no need to track anything here
      setGeneralErrorMessage(ERROR_MESSAGE_GENERAL_FAILURE);
    }
  };

  function getGdprMailingListPreferences() {
    const emailPreferences = [];

    if (inGDPR) {
      Object.keys(gdprPreferences).forEach(key => {
        const prefInput = document.getElementById(key);
        if (prefInput.checked) {
          emailPreferences.push(prefInput.value);
        }
      });
    }
    return emailPreferences;
  }
  function validateCaptcha() {
    if (!requireCaptcha || captchaValue) {
      return true;
    }

    setGeneralErrorMessage(EMPTY_RECAPTCHA);
    return false;
  }

  function resetRecaptcha() {
    setCaptchaValue(null);
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
  }

  function updateCaptchaValue() {
    if (recaptchaRef.current) {
      setCaptchaValue(recaptchaRef.current.getValue());
    }
  }

  const createAccount = async () => {
    // Perform BriteVerify email validation before creating an account.
    const briteVerifyStatus = await verifyEmail();
    if (briteVerifyStatus === '0') {
      // BriteVerify validation failed, new account cannot be created.
      // Turn off overlay layer and return early.
      setIsLoading(false);
      return;
    }
    const isEmailRisky = briteVerifyStatus === '2';

    const emailPreferences = getGdprMailingListPreferences();

    let token = '';
    try {
      // console.log('Waiting for grecaptcha to be ready');
      token = await getRecaptchaToken('signup');
      console.log('Generated token');
    } catch (error) {
      console.error('reCAPTCHA execution failed', error);
      setIsLoading(false);
      return;
    }

    try {
      const response = await register(
        email,
        password,
        emailPreferences,
        gdprInput.current && gdprInput.current.checked ? 1 : 0,
        registrationUserType,
        isEmailRisky.toString(),
        captchaValue || '',
        token || ''
      );
      if (response.status === '1') {
        let gaDimensions =
          'ga_dimensions' in response ? response.ga_dimensions : null;
        trackRegistration('signup success', 'Registered', null, gaDimensions);
        onSignupSuccess();
      } else {
        onSignupFailure(response);
      }
    } catch (e) {
      console.error(e);
      if (e.response) {
        onSignupFailure(e.response, e.code);
      }
    }
  };

  function validateEmailPresence() {
    if (email.length === 0) {
      setEmailErrorMessage(ERROR_MESSAGE_ENTER_EMAIL_ADDRESS);
      return false;
    }
    return true;
  }

  function validatePasswordPresence() {
    if (password.length === 0) {
      setPasswordErrorMessage(ERROR_MESSAGE_ENTER_PASSWORD);
      return false;
    }
    return true;
  }

  function validatePasswordLength() {
    if (password.length > 32) {
      setPasswordErrorMessage(ERROR_MESSAGE_PASSWORD_LONG);
      return false;
    }

    if (password.length < 6) {
      setPasswordErrorMessage(ERROR_MESSAGE_PASSWORD_SHORT);
      return false;
    }
    return true;
  }

  async function verifyEmail() {
    const response = await verifyEmailByBriteVerify(email);
    if (response.status === '0') {
      setEmailErrorMessage(response.msg);
    }
    return response.status;
  }

  async function emailOnBlur() {
    const response = await screenEmail(email);
    if (!response.status || response.status == '0') {
      setEmailErrorMessage(response.msg);
    } else {
      setEmailErrorMessage('');
    }
  }

  const onSubmitNewAccount = async e => {
    e.preventDefault();
    // validation
    if (!validateEmailPresence()) return;
    if (!validatePasswordPresence()) return;
    if (!validatePasswordLength()) {
      trackRegistration('Signup failure', 'failed signup', null, {
        failure_reason: 'Invalid Password',
      });
      return;
    }
    if (!validateCaptcha()) return;

    setIsLoading(true);
    resetRecaptcha();

    // First, try logging in
    try {
      const loginResponse = await localLogin(
        email,
        password,
        gdprInput.current && gdprInput.current.checked ? 1 : 0
      );
      // Successfully logged in
      if (loginResponse.status === '1') {
        trackRegistration('login success', 'Logged In');
        onSignupSuccess(true);
      } else {
        const checkResponse = await checkEmail(email);
        // Email already registered
        if (checkResponse.status) {
          onSignupFailure({ status: 0, exists: true });
        }
        // Not registered
        else {
          createAccount();
        }
      }
    } catch {
      createAccount();
    }
  };

  // RENDER
  return (
    <div className="createAccount">
      <h3>{registerTitle}</h3>
      <p className="createAccount__description">{registerDescription}</p>
      <div className="socialButtons">
        <div className="buttonWrapper">
          <FacebookLogin
            text="Sign in with Facebook"
            onSuccess={onSignupSuccess}
            onFailure={onSignupFailure}
            setIsLoading={setIsLoading}
          />
          <GoogleLoginEdCom
            onSuccess={onSignupSuccess}
            onFailure={onSignupFailure}
            setIsLoading={setIsLoading}
          />
        </div>
      </div>

      <div className="font-primary-bold align-center registration__or">OR</div>

      <form className="form registration-form" onSubmit={onSubmitNewAccount}>
        <div
          className={clsx(
            'input-row email-address ',
            emailErrorMessage && HAS_ERROR_CLASS_NAME
          )}
        >
          <FloatingLabelInput
            label="Email address"
            type="email"
            name="email"
            value={email}
            onChange={e => {
              setEmail(e.target.value);
            }}
            onBlur={e => {
              emailOnBlur(e.target.value);
            }}
          />
          <div className="errormsg">{emailErrorMessage}</div>
        </div>
        <div
          className={clsx(
            'input-row password ',
            passwordErrorMessage && HAS_ERROR_CLASS_NAME
          )}
        >
          <FloatingLabelInput
            label="Password"
            type="password"
            name="password"
            onChange={e => {
              setPassword(e.target.value);
            }}
          />
          <div className="errormsg">{passwordErrorMessage}</div>
        </div>

        {inGDPR && (
          <div className="gdpr-options" style={{ display: 'block' }}>
            <hr className="type-grade-separator" />
            <div className="email-preferences">
              <div className="newsletter-options">
                <div className="input-row ">
                  I want to hear from Education.com! Please send me messages
                  about the following (select all that apply):
                </div>
                {Object.keys(gdprPreferences).map(key => {
                  return (
                    <div className="input-row" key={key}>
                      <label className="inline" htmlFor={key}>
                        <input
                          id={key}
                          name="email-preferences[]"
                          value={key}
                          className="email-preferences"
                          type="checkbox"
                        />
                        {gdprPreferences[key]}
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {requireCaptcha && (
          <div className="recaptcha-container">
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={RECAPTCHA_SITE_KEY}
              onChange={updateCaptchaValue}
            />
            <RecaptchaV3 />
          </div>
        )}

        <div className="input-row registration__button">
          <Button type="submit">{registerButtonText}</Button>
        </div>
        <div
          className={clsx(
            'input-row general ',
            generalErrorMessage && HAS_ERROR_CLASS_NAME
          )}
        >
          <div className="errormsg">{generalErrorMessage}</div>
        </div>
        <div className="registration__login">
          Already a member?{' '}
          <a
            onClick={e => {
              e.preventDefault();
              trackRegistration('opened', 'Viewed Registration Form', 'login');
              setAccount(email);
              setSection(SECTION_SWITCH);
            }}
          >
            Log In
          </a>
        </div>

        <div className="registration__disclaimer">
          {/* eslint-disable-next-line react/no-unescaped-entities */}
          <p>
            By clicking &apos;Create Account&apos; above, I agree that I have
            read and agree to Education.com&apos;s{' '}
            <a href="/terms-of-use/">Terms of Use</a> and{' '}
            <a href="/privacy/">Privacy Policy</a>.
          </p>
          <p>
            Members receive Education.com emails. You can change email
            preferences in account settings.
          </p>
        </div>
      </form>
    </div>
  );
};
Signup.propTypes = {
  registerButtonText: PropTypes.string.isRequired,
  registerDescription: PropTypes.string.isRequired,
  registerTitle: PropTypes.string.isRequired,
  setSection: PropTypes.func.isRequired,
  gdprPreferences: PropTypes.object.isRequired,
  inGDPR: PropTypes.bool.isRequired,
  setIsLoading: PropTypes.func.isRequired,
  setAccount: PropTypes.func.isRequired,
  defaultAccount: PropTypes.string.isRequired,
  additionalInfoSection: PropTypes.string.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  registrationUserType: PropTypes.string.isRequired,
};
Signup.defaultProps = {};
export default Signup;
